import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, UserMinus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { avatarBgClass, displayNameFor, initialsFor } from "@/lib/avatarColor";
import { useToast } from "@/hooks/use-toast";

export interface CompanyMember {
  id: string;
  email: string | null;
  name: string | null;
  avatar_url?: string | null;
}

interface AssigneePickerProps {
  workOrderId: string;
  assigneeId: string | null;
  members: CompanyMember[];
  membersLoading?: boolean;
  onAssigned: (newAssigneeId: string | null) => void;
  avatarSize?: "xs" | "sm" | "md";
}

function Avatar({
  member,
  size = "md",
}: {
  member: CompanyMember | null;
  size?: "xs" | "sm" | "md";
}) {
  const dim =
    size === "xs"
      ? "w-5 h-5 text-[9px]"
      : size === "sm"
        ? "w-7 h-7 text-[10px]"
        : "w-9 h-9 text-xs";
  if (!member) {
    return (
      <div
        className={`${dim} rounded-full bg-muted text-muted-foreground flex items-center justify-center`}
      >
        <UserMinus className="w-4 h-4" />
      </div>
    );
  }
  return (
    <div
      className={`${dim} rounded-full ${avatarBgClass(
        member.id,
      )} text-white font-semibold flex items-center justify-center`}
    >
      {initialsFor(member.name, member.email)}
    </div>
  );
}

export function AssigneePicker({
  workOrderId,
  assigneeId,
  members,
  membersLoading,
  onAssigned,
  avatarSize = "sm",
}: AssigneePickerProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setQuery("");
  }, [open]);

  const current = useMemo(
    () => members.find((m) => m.id === assigneeId) || null,
    [members, assigneeId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      return (
        (m.name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
      );
    });
  }, [members, query]);

  const assign = async (newId: string | null) => {
    if (newId === assigneeId) {
      setOpen(false);
      return;
    }
    setSaving(true);
    const prev = assigneeId;
    onAssigned(newId); // optimistic
    const { error } = await supabase
      .from("work_orders")
      .update({ assignee_id: newId })
      .eq("id", workOrderId);
    setSaving(false);
    if (error) {
      onAssigned(prev);
      toast({
        title: "Couldn't update assignee",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="rounded-full ring-2 ring-transparent hover:ring-primary/40 transition"
          aria-label="Change assignee"
          title={current ? displayNameFor(current.name, current.email) : "Unassigned"}
          disabled={saving}
        >
          <Avatar member={current} size={avatarSize} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-border">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Assign to…"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          <button
            onClick={() => assign(null)}
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted text-left"
          >
            <Avatar member={null} size="sm" />
            <span className="flex-1 text-sm">No assignee</span>
            {!assigneeId && <Check className="w-4 h-4 text-primary" />}
          </button>

          {membersLoading && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Loading team…
            </div>
          )}

          {!membersLoading && filtered.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                Team members
              </div>
              {filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => assign(m.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted text-left"
                >
                  <Avatar member={m} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                      {displayNameFor(m.name, m.email)}
                    </div>
                    {m.email && m.name && (
                      <div className="text-xs text-muted-foreground truncate">
                        {m.email}
                      </div>
                    )}
                  </div>
                  {m.id === assigneeId && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </>
          )}

          {!membersLoading && filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">
              No matches
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
